/**
 * Component Registry for Rr Living Theme
 * 
 * Extends the base prestige theme registry with Rr Living specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-prestige/registry';
import { FeeTransparency } from './components/FeeTransparency';
import { InstagramSection } from './components/InstagramSection';
import { ReviewsParallax } from './components/ReviewsParallax';
import { Header } from './components/Header';
import { CommunityAmenitiesList } from './components/CommunityAmenitiesList';
import { InteriorAmenitiesList } from './components/InteriorAmenitiesList';
import { Hero } from './components/Hero';
import { HeroSolidBG } from './components/HeroSolidBG';
import { HeroSplitBG } from './components/HeroSplitBG';
import { PetPolicyHero } from './components/PetPolicyHero';
import { PetPolicy } from './components/PetPolicy';
import { PetsCallout } from './components/PetsCallout';
import { Footer } from './components/Footer';
import { PropertyOverview } from './components/PropertyOverview';
import { SitemapContent } from './components/SitemapContent';
import { ThankYouContent } from './components/ThankYouContent';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { NationalOpenHouseBanner } from './components/NationalOpenHouseBanner';
import { SpecialPopup } from './components/SpecialPopup';
import { FraudVerificationNotice } from './components/FraudVerificationNotice';

/**
 * Rr Living specific component overrides.
 * These components have been customized for the Rr Living brand.
 * 
 * Example:
 * import { Footer } from './components/Footer';
 * 
 * const rrlivingOverrides: Partial<ComponentRegistry> = {
 *   Footer,
 * };
 */
const rrlivingOverrides: Partial<ComponentRegistry> = {
  FeeTransparency,
  InstagramSection,
  ReviewsParallax,
  Header,
  CommunityAmenitiesList,
  InteriorAmenitiesList,
  Hero,
  HeroSolidBG,
  HeroSplitBG,
  PetPolicyHero,
  PetPolicy,
  PetsCallout,
  Footer,
  PropertyOverview,
  SitemapContent,
  ThankYouContent,
  SpecialOfferBanner,
  NationalOpenHouseBanner,
  SpecialPopup,
  FraudVerificationNotice,
};

/**
 * Final merged registry for Rr Living properties.
 * Inherits all components from the prestige theme, with Rr Living overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...rrlivingOverrides,
};

export default registry;
